import React, { useContext, useEffect } from 'react';
import { KibIngressCard } from '@chewy/kib-cards-react';
import '@chewy/kib-cards-styles/dist/styles/kib-ingress-card.css';
import { KibPharmacyIcon } from '@chewy/kib-icons-react';
import { ApiPetPrescription } from 'api/generated-client/PetProfile-Service-A.v1/models';
import analyticsContext from '../../context/AnalyticsContext';
import { AnimalTypeId, PetInterface } from '../../types/pet';
import { MedicalLabels } from '../pet-form/MedicalNew/MedicalLabels';
import { PII_CLASSNAME } from '../../util/helpers';
import { getPetFields } from './util/getFields';
import './petProfileDetails.scss';

const PLACEHOLDER_TEXT = 'Not added yet';

export const isPIIInfo = (label: string): boolean =>
  [
    MedicalLabels.labelMedicalConditions as string,
    MedicalLabels.labelMedicationAllergies as string,
    MedicalLabels.labelMedications as string,
  ].includes(label);

const renderLabelAndValue = (label: string, value?: string | number | null) => (
  <div className="pet-profile__detail--item" data-testid={`pet-profile-detail-${label}`}>
    <h3
      className="pet-profile__detail--label pet-profile__detail--heading"
      data-testid={`pet-profile-detail-dt-${label}`}
    >
      {label}
    </h3>
    <div className="pet-profile__detail--value" data-testid={`pet-profile-detail-dd-${label}`}>
      {value || PLACEHOLDER_TEXT}
    </div>
  </div>
);

const PetProfileDetails = ({
  petProfile,
  petPrescriptions,
}: {
  petProfile: PetInterface;
  petPrescriptions?: ApiPetPrescription[];
}) => {
  const healthHeader = 'Health';
  const basicsHeader = 'Basics';
  const prescriptionsHeader = 'Prescriptions';
  const prescriptionsSubheader = 'Purchased through Chewy';

  const analytics = useContext(analyticsContext);

  useEffect(() => {
    analytics.trackEvent('new-pet-profile', 'view-details', null, null, {
      sourceView: 'pet-profile-v2',
      petId: petProfile.id,
    });
  }, [petProfile.id, analytics]);

  const [healthFields, basicFields] = getPetFields(petProfile);

  const handlePrescriptionClick = () => {
    window.location.href = '/pethealth/my-prescriptions';
  };

  const hasPrescriptions =
    petPrescriptions &&
    petPrescriptions.length > 0 &&
    petProfile.petType.id !== AnimalTypeId.farmAnimal
      ? true
      : false;

  return (
    <>
      <h2
        className="pet-profile__details--section-header"
        data-testid="pet-profile-details-section-health"
      >
        {healthHeader}
      </h2>
      {hasPrescriptions && (
        <KibIngressCard
          icon={<KibPharmacyIcon />}
          subtitle={prescriptionsSubheader}
          title={prescriptionsHeader}
          onClick={handlePrescriptionClick}
          data-testid="pet-profile-prescription-ingress"
        />
      )}
      {healthFields.map(({ label, value }) => (
        <React.Fragment key={`${label}${value}`}>
          {isPIIInfo(label) ? (
            <div className={PII_CLASSNAME} data-hj-suppress data-testid="pii-hotjar">
              {renderLabelAndValue(label, value)}
            </div>
          ) : (
            renderLabelAndValue(label, value)
          )}
        </React.Fragment>
      ))}
      <h2
        className="pet-profile__details--section-header"
        data-testid="pet-profile-details--section-basics"
      >
        {basicsHeader}
      </h2>
      {basicFields.map(({ label, value }) => (
        <React.Fragment key={`${label}${value}`}>
          {isPIIInfo(label) ? (
            <div className={PII_CLASSNAME} data-hj-suppress data-testid="pii-hotjar">
              {renderLabelAndValue(label, value)}
            </div>
          ) : (
            renderLabelAndValue(label, value)
          )}
        </React.Fragment>
      ))}
    </>
  );
};

export default PetProfileDetails;
